<template>
  <div class="price-breakdown clearfix">
    <div class="callout callout-info special-rates" v-if="priceOptions.hasOvernight || priceOptions.hasWeekend">
      <strong>This car has discount rates enabled</strong><br/><br/>
      <p v-if="priceOptions.hasWeekend && priceOptions.rate">
        <strong><span class="fa fa-star"></span> Weekend Rates</strong><br/>
        Start: Friday {{ priceOptions.weekendRateStart | time_format }}<br/>
        End: Monday {{ priceOptions.weekendRateEnd | time_format }} <br/>
        Included KMs: {{ priceTable.opts.Rate.includeKms * 2 }}KM<br/>
        Rate: {{ priceOptions.rate.weekendRate | to_dollar_amount }}<br/>
      </p>
      <p v-if="priceOptions.hasOvernight && priceOptions.rate">
        <strong><span class="fa fa-moon"></span> Overnight Rates</strong><br/>
        Start: {{ priceOptions.overnightRateStart | time_format }}<br/>
        End: {{ priceOptions.overnightRateEnd | time_format }}<br/>
        Included KMs: 0KM<br/>
        Rate: {{ priceOptions.rate.overnightRate | to_dollar_amount }}<br/>
        <em>
          Overnight rate only applies <strong>between</strong>
          this period and must be booked for a minimum of 6 hours.
          If the booking exceeds either the start or end time then standard
          rates will be charged for the entire booking.
        </em>
      </p>
    </div>
    <div class="form-section-info">
      Prices are automatically calculated at their lowest possible rate
    </div>
    <p>
      Booking from <strong>{{ priceOptions.startTime | date_format(priceOptions.timezone) }}</strong> to
      <strong>{{ priceOptions.endTime | date_format(priceOptions.timezone) }}</strong>.
    </p>
    <price-table v-if="priceTable"
      :price-table="priceTable"
      :show-details="priceOptions.showDetails"
      :use-credits="useCredits">
    </price-table>
    <p class="alert alert-danger" v-if="priceOptions.remainingCredit < 0">
      Minimum purchase amount is $5.00. Should you go ahead with this
      transaction, the customer will be debited
      {{ Math.abs(priceOptions.remainingCredit) | to_dollar_amount }} from
      their credits and will not be able to make subsequent bookings until this
      balance has been paid.
    </p>
    <div class="alert alert-info small">
      Please note additional charges may be applied at the completion of the
      booking:
      <ul v-if="priceTable">
        <li>Each kilometre travelled is billed at ${{ priceTable.opts.Rate.perKmCost }}/km.</li>
        <li>{{ priceTable.opts.Rate.includeKms }}km is only included when a booking uses the daily rate.</li>
        <li>{{ priceTable.opts.Rate.includeKms }}km is included for each daily rate charged to the booking.</li>
        <li>Should the member return the car late, every 15 min over the booking end time will incur an additional hour charged.</li>
        <li>Any tolls used within the month, will be billed to the reservation the following month.</li>
      </ul>
    </div>
    <p class="alert alert-warning" v-if="preAuthRequired">
      Member will be charged <strong>{{ options.preAuth.amount | to_dollar_amount }}
      pre-authorisation</strong>.
    </p>
    <p class="alert alert-warning" v-if="!preAuthRequired && options.preAuth">
      Member is not required to pay a pre-authorisation for this booking.
    </p>
    <p class="form-section-info" v-if="priceOptions.businessReservation">
      By selecting a business credit card, this booking is a business
      booking. All charges will be made to the account owner's business
      credit card. Account credits can not be used to pay for this booking.
    </p>
  </div>
</template>

<script>
  import PriceTable from '@/components/displays/PriceTable'
  import Util from '@server/util'
  import {
    LOAD_CAR,
    LOAD_CAR_RATES,
    LOAD_CAR_EXTRAS
  } from '@/vuex/types'
  import { mapGetters } from 'vuex'

  export default {
    name: 'PriceBreakdown',
    components: {
      PriceTable
    },
    props: {
      options: {
        type: Object
      }
    },
    computed: {
      ...mapGetters([
        'carRates',
        'carExtras',
        'loadedCar',
        'loadedReservation'
      ]),
      preAuthRequired () {
        if (this.options.preAuth) {
          return !this.options.preAuth.onFile && this.options.preAuth.amount > 0
        } else {
          return false
        }
      },
      useCredits () {
        let car = this.loadedCar.uuid ? this.loadedCar : this.loadedReservation.Car
        if (car && car.Organization.useCredits) {
          return !this.priceOptions.businessReservation
        }
        return false
      },
      priceOptions () {
        let businessReservation = false
        if (this.options.businessCards.length) {
          let usingBusinessCard = this.options.businessCards.find((el) => {
            return el.uuid === this.payment.uuid
          })
          businessReservation = Boolean(usingBusinessCard)
        }
        let rate = this.options.rate
        if (this.rate) {
          rate = this.carRates.find(el => el.uuid === this.rate)
        }
        return {
          hasOvernight: false,
          hasWeekend: false,
          car: {} || this.options.car,
          driver: {} || this.options.driver,
          timezone: 'Australia/Melbourne',
          businessReservation,
          businessCards: [] || this.options.businessCards,
          remainingCredit: 0,
          ...this.options,
          startTime: this.startTime || this.options.startTime,
          endTime: this.endTime || this.options.endTime,
          rate
        }
      },
      priceTable () {
        // check for a matched promo
        let promo = false
        if (this.promo) {
          promo = this.options.promos.find((el) => {
            return el.code.toUpperCase() === this.promo.toUpperCase()
          })
        }
        let carRate = this.carRates.find((el) => {
          return el.uuid === this.rate
        })
        // if there is a promo, provide a separate calculation
        let percentDiscount = 0
        if (promo) {
          percentDiscount = promo.percentDiscount
        }
        if (this.options.memberDiscount) {
          if (this.options.memberDiscount > percentDiscount) percentDiscount = this.options.memberDiscount
        }
        let extras = this.carExtras.filter((el) => {
          return this.extras.includes(el.uuid)
        })
        if (carRate && (this.loadedCar.uuid || this.loadedReservation.uuid)) {
          let Car = this.loadedCar.uuid ? this.loadedCar : this.loadedReservation.Car
          let Lot = this.loadedCar.uuid ? this.loadedCar.Lot : this.loadedReservation.Lot
          return Util.calculateCosts({
            Organization: Car.Organization,
            Car,
            Lot,
            Reservation: this.loadedReservation ? this.loadedReservation : false,
            Account: this.priceOptions.driver,
            Rate: carRate,
            startDate: this.priceOptions.startTime,
            endDate: this.priceOptions.endTime,
            extras,
            percentDiscount,
            useCredits: this.useCredits
          })
        } else {
          return false
        }
      }
    },
    data () {
      return {
        startTime: false,
        endTime: false,
        rate: false,
        payment: false,
        extras: [],
        promo: false,
        isValid: true
      }
    },
    async mounted () {
      await this.$store.dispatch(LOAD_CAR, {
        carUuid: this.options.car.uuid,
        startTime: this.options.startTime,
        endTime: this.options.endTime
      })
      await this.$store.dispatch(LOAD_CAR_RATES)
      await this.$store.dispatch(LOAD_CAR_EXTRAS, this.options.car.uuid)
    },
    methods: {
      runWatcher (val, name) {
        switch (name) {
          case 'dateRange':
            this.startTime = val.startDate
            this.endTime = val.endDate
            break
          case 'rates':
            this.rate = val
            break
          case 'payment':
            this.payment = val
            break
          case 'extras':
            this.extras = val
            break
          case 'promo':
            this.promo = val
            break
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/displays/PriceBreakdown.vue